/** Abbreviations after which a full stop does not end the sentence. Titles always join the next word;
 * the others join it only when it starts in lower case or with a digit ("p.m. and", "etc. 5"). */
const TITLE_ABBR = /(?:^|[\s("'])(?:Mr|Mrs|Ms|Mx|Dr|Prof|Sr|Jr|St|Mt|Rev|Hon|Gen|Col|Capt|Lt|Sgt|Gov|Sen|Rep|Fr|Messrs|Mmes|Dr|No|Fig|Vol|Ch|Sec|Dept|Est|Approx)\.$/i;
const SOFT_ABBR = /(?:^|[\s("'])(?:e\.g|i\.e|a\.m|p\.m|vs|etc|cf|viz|inc|ltd|co|corp|jr|sr|u\.s|u\.k|ph\.d)\.$/i;
const INITIAL = /(?:^|\s)[A-Z]\.$/;

function joinsNext(prev: string, next: string): boolean {
  const p = prev.trimEnd();
  const n = next.trimStart();
  if (!n) return false;
  if (/^(?:e\.g|i\.e)\.$/i.test(p.split(/\s+/).pop() ?? "")) return true;
  if (TITLE_ABBR.test(p) || INITIAL.test(p)) return /^[A-Z0-9"'(]/.test(n);
  if (SOFT_ABBR.test(p)) return /^[a-z0-9]/.test(n);
  // A full stop with a digit right before and after is a decimal point or a date, never a sentence end.
  if (/\d\.$/.test(p) && /^\d/.test(n)) return true;
  return false;
}

/** Sentence segmentation with Intl.Segmenter where available, then a guard that re-joins pieces that were split
 * after an abbreviation ("Dr. Smith", "3:30 p.m.") or inside a number ("$1,234.56"). */
export function splitSentences(text: string): string[] {
  let pieces: string[];
  const Seg = (Intl as unknown as { Segmenter?: new (l: string, o: { granularity: string }) => { segment(t: string): Iterable<{ segment: string }> } }).Segmenter;
  if (Seg) {
    pieces = Array.from(new Seg("en", { granularity: "sentence" }).segment(text), (x) => x.segment);
  } else {
    pieces = text.split(/(?<=[。！？])|(?<=[.!?।]["')\]]*)\s+/).filter(Boolean);
  }
  const out: string[] = [];
  for (const piece of pieces) {
    if (out.length && joinsNext(out[out.length - 1], piece)) out[out.length - 1] += piece.startsWith(" ") || /\s$/.test(out[out.length - 1]) ? piece : ` ${piece}`;
    else out.push(piece);
  }
  return out;
}

/** Splits text into sentence-sized chunks (at most maxLen characters) so long articles are not sent to the
 * browser as one huge utterance (Chrome's online voices stop after about 15 seconds of it). */
export function splitIntoChunks(text: string, maxLen = 200): string[] {
  const out: string[] = [];
  const paragraphs = text.replace(/\r/g, "").split(/\n+/);
  for (const para of paragraphs) {
    const sentences = splitSentences(para);
    for (const raw of sentences) {
      let s = raw.trim();
      if (!s) continue;
      while (s.length > maxLen) {
        // Break long sentences at a comma/semicolon/space near the limit.
        let cut = Math.max(s.lastIndexOf(",", maxLen), s.lastIndexOf(";", maxLen), s.lastIndexOf(" ", maxLen));
        if (cut < maxLen * 0.4) cut = maxLen;
        out.push(s.slice(0, cut + 1).trim());
        s = s.slice(cut + 1).trim();
      }
      if (s) out.push(s);
    }
  }
  return out;
}

export type VoiceLike = { name: string; lang: string; default?: boolean; localService?: boolean; voiceURI?: string };

/** The voice that matches the person's language: exact language-region, then the same language,
 * preferring the browser's default and on-device voices. Falls back to the default voice, then the first. */
export function pickDefaultVoice<V extends VoiceLike>(voices: V[], languages: readonly string[]): V | undefined {
  if (voices.length === 0) return undefined;
  const norm = (l: string) => l.toLowerCase().replace("_", "-");
  const rank = (v: V) => (v.default ? 0 : 1) + (v.localService ? 0 : 0.5);
  const best = (list: V[]) => [...list].sort((a, b) => rank(a) - rank(b))[0];
  for (const lang of languages) {
    const l = norm(lang);
    const exact = voices.filter((v) => norm(v.lang) === l);
    if (exact.length) return best(exact);
    const base = l.split("-")[0];
    const same = voices.filter((v) => norm(v.lang).split("-")[0] === base);
    if (same.length) return best(same);
  }
  return voices.find((v) => v.default) ?? voices[0];
}

/** Language code suggested by the writing system of the text, or null for Latin-script text. */
export function scriptLanguage(text: string): string | null {
  if (/[ऀ-ॿ]/.test(text)) return "hi";
  if (/[぀-ヿ]/.test(text)) return "ja";
  if (/[가-힯]/.test(text)) return "ko";
  if (/[一-鿿]/.test(text)) return "zh";
  if (/[؀-ۿ]/.test(text)) return "ar";
  if (/[Ѐ-ӿ]/.test(text)) return "ru";
  if (/[஀-௿]/.test(text)) return "ta";
  if (/[ঀ-৿]/.test(text)) return "bn";
  if (/[ఀ-౿]/.test(text)) return "te";
  if (/[฀-๿]/.test(text)) return "th";
  return null;
}

/** Rough listening time in seconds at 150 words per minute. */
export function listeningSeconds(text: string, rate: number): number {
  const t = text.trim();
  const words = t ? t.split(/\s+/).length : 0;
  // Unbroken text (or a script without spaces) counts as one "word", so fall back to characters (about 900 per minute).
  const minutes = Math.max(words / 150, t.length / 900);
  return Math.round(minutes * 60 / Math.max(0.1, rate));
}
